/**
 * Contrat de donnees partage entre le serveur et le client.
 *
 * Ce dossier n'est PAS un workspace npm : il est compile directement par les
 * deux cotes (voir server/tsconfig.json et l'alias `@shared` de Vite).
 *
 * Contraintes volontaires :
 *  - aucune dependance, ni Node, ni DOM ;
 *  - UN SEUL fichier, sans import interne. Le serveur resout en NodeNext
 *    (extensions `.js` obligatoires) la ou Vite resout en bundler : eviter
 *    tout import croise supprime le probleme a la racine.
 */

// ---------------------------------------------------------------------------
//  Langues
// ---------------------------------------------------------------------------

/** Langues supportees par l'interface et par le contenu. FR est la langue pivot. */
export const SUPPORTED_LANGS = ["fr", "en"] as const;

export type Lang = (typeof SUPPORTED_LANGS)[number];

/** Langue de repli quand une traduction manque. Jamais autre chose que FR. */
export const FALLBACK_LANG: Lang = "fr";

/**
 * Texte multilingue tel que stocke en base dans les colonnes `*_i18n`
 * (JSON `{"fr": "...", "en": "..."}`). FR est obligatoire, le reste optionnel.
 */
export type I18nText = { fr: string } & Partial<Record<Lang, string>>;

/** Vrai si la valeur correspond a une langue supportee. */
export function isLang(value: unknown): value is Lang {
  return typeof value === "string" && (SUPPORTED_LANGS as readonly string[]).includes(value);
}

/** Valide la forme d'un texte multilingue (FR present et non vide). */
export function isI18nText(value: unknown): value is I18nText {
  if (typeof value !== "object" || value === null) return false;
  const fr = (value as Record<string, unknown>)["fr"];
  return typeof fr === "string" && fr.trim() !== "";
}

/**
 * Choisit la traduction demandee, avec repli sur le francais.
 * Une traduction vide ou blanche est traitee comme absente.
 */
export function pickLang(text: I18nText, lang: Lang): string {
  const wanted = text[lang];
  if (typeof wanted === "string" && wanted.trim() !== "") return wanted;
  return text.fr; // FALLBACK_LANG, mais lu directement : seul `fr` est garanti non-optionnel
}

/**
 * Parse une colonne `*_i18n` lue en base. Leve si le JSON est invalide ou si
 * le francais manque : c'est une corruption de contenu, pas un cas nominal.
 */
export function parseI18n(raw: string): I18nText {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error(`Champ i18n illisible (JSON invalide) : ${raw.slice(0, 80)}`);
  }
  if (!isI18nText(parsed)) {
    throw new Error(`Champ i18n sans traduction francaise : ${raw.slice(0, 80)}`);
  }
  return parsed;
}

// ---------------------------------------------------------------------------
//  Contrat d'API
// ---------------------------------------------------------------------------

/** Reponse de `GET /api/health`. Sert de preuve de vie env -> base -> HTTP. */
export interface HealthResponse {
  ok: boolean;
  version: string;
  /** Horodatage serveur, ISO 8601 UTC (convention de tout le projet). */
  now: string;
  db: {
    /** Nombre de tables trouvees dans la base (11 avec schema.sql complet). */
    tables: number;
    /** Confirme que `PRAGMA foreign_keys` est bien actif sur la connexion. */
    foreignKeys: boolean;
  };
}

// ---------------------------------------------------------------------------
//  Authentification
// ---------------------------------------------------------------------------

/**
 * Le joueur tel que le client le connait. AUCUNE donnee personnelle : un
 * identifiant technique et un pseudo choisi, rien d'autre.
 * Le NIP, meme hashe, ne quitte jamais le serveur.
 */
export interface SessionUser {
  id: string;
  pseudo: string;
}

/** Contraintes de saisie, partagees pour valider des DEUX cotes. */
export const PSEUDO_MIN_LENGTH = 2;
export const PSEUDO_MAX_LENGTH = 30;
export const NIP_LENGTH = 4;

/** Lettres (accents compris), chiffres, espace, tiret et souligne. */
const PSEUDO_PATTERN = /^[\p{L}\p{N} _-]+$/u;

/** Normalise un pseudo saisi : espaces de bord retires, espaces internes reduits. */
export function normalizePseudo(raw: string): string {
  return raw.trim().replace(/\s+/g, " ");
}

export type PseudoProblem = "tooShort" | "tooLong" | "badChars";

/** Pourquoi un pseudo est refuse, ou `null` s'il convient. Sert a l'expliquer au jeune. */
export function pseudoProblem(raw: string): PseudoProblem | null {
  const pseudo = normalizePseudo(raw);
  if (pseudo.length < PSEUDO_MIN_LENGTH) return "tooShort";
  if (pseudo.length > PSEUDO_MAX_LENGTH) return "tooLong";
  if (!PSEUDO_PATTERN.test(pseudo)) return "badChars";
  return null;
}

export function isValidPseudo(raw: string): boolean {
  return pseudoProblem(raw) === null;
}

/** Un NIP est exactement 4 chiffres. Ni plus, ni moins, ni autre chose. */
export function isValidNip(raw: string): boolean {
  return new RegExp(`^\\d{${NIP_LENGTH}}$`).test(raw);
}

/** Corps de `POST /api/auth/login` et `/api/auth/register`. */
export interface LoginRequest {
  pseudo: string;
  nip: string;
}

/** Reponse de `/api/auth/login`, `/register` et `/me` (user vaut null si deconnecte). */
export interface SessionResponse {
  user: SessionUser | null;
}

/** Corps de `POST /api/admin/login`. */
export interface AdminLoginRequest {
  password: string;
}

/** Reponse des routes d'administration liees a la session. */
export interface AdminSessionResponse {
  admin: boolean;
}

/**
 * Codes d'erreur d'authentification. Le client s'en sert pour choisir une cle
 * i18n : les messages destines au jeune ne viennent jamais du serveur.
 */
export const AUTH_ERROR = {
  /** Le pseudo n'existe pas encore : le client propose de creer le compte. */
  unknownPseudo: "unknown_pseudo",
  /** Pseudo connu mais NIP faux. Volontairement indistinct d'un NIP vide. */
  badCredentials: "bad_credentials",
  pseudoTaken: "pseudo_taken",
  invalidPseudo: "invalid_pseudo",
  invalidNip: "invalid_nip",
  tooManyAttempts: "too_many_attempts",
  notAuthenticated: "not_authenticated",
  adminRequired: "admin_required",
  statsRequired: "stats_required",
} as const;

export type AuthErrorCode = (typeof AUTH_ERROR)[keyof typeof AUTH_ERROR];

// ---------------------------------------------------------------------------
//  Contenu de jeu — API d'administration
//
//  ATTENTION : ces formes ne circulent QUE sur les routes /api/admin, protegees
//  par requireAdmin. `targetAppObjectId` est la bonne reponse : elle ne doit
//  jamais apparaitre dans une reponse destinee a l'app du jeune.
// ---------------------------------------------------------------------------

/** Un code d'objet reel : exactement 4 chiffres. */
export const CODE_LENGTH = 4;

export function isValidCode(raw: string): boolean {
  return new RegExp(`^\\d{${CODE_LENGTH}}$`).test(raw);
}

/**
 * Les deux series d'astres de la mission 2.
 *
 * - « officielles » : les planches fournies, detourees. SEPT astres, un seul
 *   leurre (la Terre) — Venus et le Soleil n'existent pas dans cette serie.
 * - « ai » : les neuf astres dessines en SVG, dont TROIS leurres.
 *
 * Le nombre de leurres change donc la difficulte : avec un seul, la derniere
 * dragouille se joue a pile ou face entre les deux astres restants. C'est
 * assume, mais c'est a savoir avant de choisir.
 *
 * Les deux series utilisent les MEMES identifiants de fichier (mercure, mars,
 * …), donc les memes bonnes reponses : basculer de l'une a l'autre ne change
 * que les images.
 */
export const PLANET_SETS = ["officielles", "ai"] as const;
export type PlanetSet = (typeof PLANET_SETS)[number];
export const DEFAULT_PLANET_SET: PlanetSet = "officielles";

export function isPlanetSet(value: unknown): value is PlanetSet {
  return typeof value === "string" && (PLANET_SETS as readonly string[]).includes(value);
}

export interface AdminEvent {
  id: string;
  name: string;
  /** ISO 8601 UTC, ou null si la periode n'est pas bornee. */
  startsAt: string | null;
  endsAt: string | null;
  isActive: boolean;
  /** La bascule vers le mode Explorateur est-elle proposee au jeune ? */
  explorerModeEnabled: boolean;
  /** Quelle serie d'astres la mission 2 propose. */
  planetSet: PlanetSet;
  /** Le concours de fin est-il propose sur cette mission ? */
  contestEnabled: boolean;
  createdAt: string;
}

export interface EventInput {
  name: string;
  startsAt?: string | null;
  endsAt?: string | null;
  isActive?: boolean;
  explorerModeEnabled?: boolean;
  /**
   * Serie d'astres de la mission 2. A la MODIFICATION, en changer reconstruit
   * les astres de la mission : voir `applyPlanetSet`.
   */
  planetSet?: PlanetSet;
  /** Proposer le concours de fin. Faux par defaut : toutes les fetes n'en ont pas. */
  contestEnabled?: boolean;
  /**
   * Cree d'emblee un objet par image du kit, avec les noms proposes.
   * Vrai par defaut : il n'y a qu'un kit, et les activer un par un serait dix
   * clics pour rien. Creation seulement — ignore lors d'une modification.
   */
  seedKitObjects?: boolean;
}

export interface AdminSeries {
  id: string;
  eventId: string;
  nameI18n: I18nText;
  /** Emoji ou court libelle decoratif. */
  icon: string | null;
  sortOrder: number;
}

export interface SeriesInput {
  nameI18n: I18nText;
  icon?: string | null;
  sortOrder?: number;
}

export interface AdminAppObject {
  id: string;
  seriesId: string;
  nameI18n: I18nText;
  descriptionI18n: I18nText | null;
  /** URL prete a l'emploi (`/uploads/...`), ou null si aucune image. */
  imageUrl: string | null;
  sortOrder: number;
}

/** Les champs texte d'un objet de l'app. L'image passe en multipart, a part. */
export interface AppObjectInput {
  nameI18n: I18nText;
  descriptionI18n?: I18nText | null;
  sortOrder?: number;
  /**
   * Identifiant d'une image de la bibliotheque integree, au lieu d'un fichier
   * televerse. Les deux sont exclusifs ; un fichier envoye l'emporte.
   */
  libraryImageId?: string | null;
}

export interface AdminRealObject {
  id: string;
  eventId: string;
  seriesId: string;
  code: string;
  /** LA bonne reponse. Strictement reservee a l'administration. */
  targetAppObjectId: string;
  labelAdmin: string | null;
}

export interface RealObjectInput {
  code: string;
  seriesId: string;
  targetAppObjectId: string;
  labelAdmin?: string | null;
}

/**
 * Une image de la bibliotheque integree : les planches du kit physique,
 * livrees avec l'application. L'admin les active sans rien televerser.
 */
export interface LibraryImage {
  /** Identifiant stable, tire du nom de fichier (ex. « objet-03 »). */
  id: string;
  /** URL directement affichable. */
  url: string;
  /** Vrai si un objet de l'app utilise deja cette image dans cet event. */
  used: boolean;
  /** Nom propose, qui pre-remplit le formulaire. Modifiable par l'admin. */
  suggestedName: I18nText | null;
}

// ---------------------------------------------------------------------------
//  Textes de la mission, modifiables depuis l'administration
//
//  Stockes en table cle/valeur plutot qu'en colonnes : ajouter un texte plus
//  tard ne demandera aucune migration.
// ---------------------------------------------------------------------------

export const EVENT_TEXT_KEYS = [
  // En tete : ils s'affichent des l'ecran de connexion, avant toute
  // authentification, et coiffent toutes les pages.
  "app_title",
  "app_subtitle",
  "welcome_title",
  "welcome_body",
  "mission1_title",
  "mission1_body",
  "mission2_title",
  "mission2_body",
  // Concours : visibles seulement une fois les deux missions terminees, et
  // seulement si l'admin a active le concours sur la mission.
  "contest_title",
  "contest_body",
] as const;

export type EventTextKey = (typeof EVENT_TEXT_KEYS)[number];

export function isEventTextKey(value: unknown): value is EventTextKey {
  return typeof value === "string" && (EVENT_TEXT_KEYS as readonly string[]).includes(value);
}

/** Tous les textes d'une mission, dans les deux langues. */
export type EventTexts = Record<EventTextKey, I18nText>;

/**
 * Valeurs par defaut, posees a la creation d'une mission.
 * L'admin les modifie ensuite librement ; ce ne sont que des points de depart.
 */
export const DEFAULT_EVENT_TEXTS: EventTexts = {
  app_title: { fr: "Mission dragouilles", en: "Mission dragouilles" },
  app_subtitle: {
    // Majuscule au seul premier mot : c'est le developpement de « ASD », et
    // c'est ainsi qu'il s'ecrit dans le mot de bienvenue. Les deux etaient
    // ecrits differemment jusqu'ici, ce qui se remarquait d'une page a l'autre.
    fr: "L'Agence spatiale dragouille",
    en: "The Dragouille Space Agency",
  },
  welcome_title: { fr: "Bienvenue", en: "Welcome" },
  welcome_body: {
    // ASD se developpe en « Agence spatiale dragouille » : majuscule au premier
    // mot seulement, comme le nom d'une institution en francais.
    // « l'accueil » désigne le comptoir du cosmodôme, pas l'écran d'accueil de
    // l'application. L'ambiguïté est acceptable : l'app ne parle plus d'accueil
    // nulle part, son bouton de retour dit « Retour aux expéditions ».
    fr: "Bonjour dragonaute !\n\nBienvenue à l'ASD, l'Agence spatiale dragouille.\n\nVoici le plan de ta mission patato-cosmique qui comprend deux expéditions. 5-4-3-2-1-0... décollage !\n\nUne fois la mission terminée, rends-toi à l'accueil pour chercher ta surprise (jusqu'à épuisement des stocks) et participe au concours !",
    // « dragonaute » est un mot-valise (dragouille + astronaute) : « dragonaut »
    // le rend en anglais, la mécanique du jeu de mots restant lisible.
    // ASD reste ASD en anglais, et « Agence spatiale dragouille » n'est PAS
    // traduit (pas de « Dragouille Space Agency »). Entre guillemets doubles
    // SEULEMENT en anglais, comme une citation d'un nom propre francais dans
    // une phrase anglaise ; le francais n'en a pas besoin, il l'ecrit tel
    // quel. Revenu sur DSA apres une deuxieme relecture de l'organisatrice.
    en: "Hello dragonaut!\n\nWelcome to the ASD, the \"Agence spatiale dragouille\".\n\nHere's your potato-cosmic mission plan, with two expeditions inside. 5-4-3-2-1-0... lift-off!\n\nOnce the mission is over, head to the front desk to pick up your surprise (while supplies last) and enter the sweepstakes!",
  },
  // « Expédition » et non « mission » : il n'y a qu'UNE mission, qui se joue en
  // DEUX expéditions. Vocabulaire arrêté après la seconde présentation.
  mission1_title: { fr: "Expédition 1 — Grand ménage", en: "Expedition 1 — Big clean-up" },
  // « 10 objets » est annoncé en clair, et c'est voulu depuis les leurres : la
  // grille en montre vingt, et le jeune doit savoir combien il en cherche —
  // c'est le même nombre que le « 7 / 10 » de la barre de progression.
  mission1_body: {
    fr: "Lors de différentes sorties dans l'espace, les dragouilles ont échappé 10 objets. Pars à leur recherche dans le Cosmodôme et coche chaque objet que tu repères. Attention ! Ne coche que ce que tu vois vraiment de tes propres yeux, car des intrus se sont glissés dans la liste.",
    en: "On various trips into space, the dragouilles dropped 10 objects. Go looking for them around the Cosmodôme and tick each one you spot. Watch out! Only tick what you can actually see with your own eyes, because a few sneaky decoys are hiding in the list.",
  },
  mission2_title: { fr: "Expédition 2 — Top planète", en: "Expedition 2 — Top planet" },
  mission2_body: {
    fr: "Trouve quelle est la planète préférée de chaque dragouille. Parcours le Cosmodôme pour trouver les réponses et clique sur chaque personnage pour lui associer la bonne planète.",
    en: "Find out which planet each dragouille likes best. Explore the Cosmodôme to find the answers, then tap each character to match it with the right planet.",
  },
  contest_title: {
    fr: "Participation au concours",
    en: "Sweepstakes entry",
  },
  // Texte NEUTRE et volontairement sobre : c'est le seul endroit de l'app qui
  // demande une donnee personnelle, et il s'adresse a des 6-12 ans. On demande
  // donc l'adresse d'un PARENT, et on dit a quoi elle sert avant de la
  // demander, pas apres.
  contest_body: {
    // La liste des lots s'ecrit avec des tirets, en clair : ce texte est rendu
    // en `white-space: pre-wrap`, donc les sauts de ligne et les tirets
    // s'affichent tels quels. Pas de Markdown ici, contrairement a la page de
    // consentement.
    fr: "Bravo ! Mission terminée ! Tu peux maintenant participer au concours et courir la chance de gagner ce prix spatial :\n\n• Un laissez-passer familial pour le Cosmodôme (2 adultes, 2 enfants);\n• 5 livres dragouilles de ton choix.\n\nDemande à un adulte de saisir son adresse courriel. Celle-ci servira uniquement à prévenir la famille dragonaute gagnante, puis elle sera supprimée. Une seule participation par adresse.\n\nLe concours se termine le 2026-10-26 à 13 h.",
    en: "Well done — mission complete! You can now enter the sweepstakes for a chance to win this out-of-this-world prize:\n\n• A family pass for the Cosmodôme (2 adults, 2 children);\n• 5 dragouilles books of your choice.\n\nAsk an adult to enter their email address. It will only be used to contact the winning dragonaut family, and will be deleted afterwards. One entry per address.\n\nThe sweepstakes ends on 2026-10-26 at 13:00.",
  },
};

// ---------------------------------------------------------------------------
//  Le jeu, cote jeune
//
//  ATTENTION : rien ici ne doit reveler la bonne reponse d'un objet reel.
//  Ces formes circulent sur /api/game, accessible a tout jeune connecté.
// ---------------------------------------------------------------------------

/** Les deux modes de jeu. */
export const GAME_MODES = ["adventurer", "explorer"] as const;
export type GameMode = (typeof GAME_MODES)[number];

export function isGameMode(value: unknown): value is GameMode {
  return typeof value === "string" && (GAME_MODES as readonly string[]).includes(value);
}

/** Un objet tel que le jeune le voit : image, nom, description. Rien d'autre. */
export interface GameObject {
  id: string;
  seriesId: string;
  nameI18n: I18nText;
  descriptionI18n: I18nText | null;
  imageUrl: string | null;
  sortOrder: number;
  /** Vrai si le jeune l'a deja mis dans son sac pendant cette mission. */
  found: boolean;
  /** Horodatage de la trouvaille, ISO 8601 UTC. */
  foundAt: string | null;
}

export interface GameSeries {
  id: string;
  nameI18n: I18nText;
  icon: string | null;
  sortOrder: number;
}

/** La mission en cours ou terminee. */
export interface GameMission {
  id: string;
  /** De quel jeu relève cette partie : mission 1 ou mission 2. */
  game: Game;
  mode: GameMode;
  /** ISO 8601 UTC : le clic sur « Depart », pas la premiere trouvaille. */
  startedAt: string;
  /** null tant que le jeune n'a pas clique sur « Fin ». */
  endedAt: string | null;
}

/**
 * Etat complet du jeu pour le jeune connecte, en un seul appel.
 * `event` vaut null quand aucune mission n'est ouverte : le jeune voit alors
 * « la chasse n'est pas encore ouverte ».
 */
export interface GameStateResponse {
  event: { id: string; name: string; explorerModeEnabled: boolean } | null;
  mission: GameMission | null;
  series: GameSeries[];
  objects: GameObject[];
  /**
   * Nombre d'objets AFFICHES — leurres compris. Ce n'est pas le nombre a
   * trouver : voir `objectsToFind`.
   */
  total: number;
  /**
   * Nombre d'objets COCHES, justes et faux confondus.
   *
   * Surtout ne pas n'y compter que les bons : la barre ne bougerait pas en
   * cochant un leurre, et il suffirait de regarder le compteur pour les
   * identifier un par un. Ce compteur dit ce que le jeune a fait, pas ce qu'il
   * a reussi — le bilan de fin s'en charge.
   */
  found: number;
  /**
   * Combien d'objets il y a REELLEMENT a trouver (les leurres exclus).
   *
   * Sans danger a publier : le jeu annonce de toute facon qu'il y a des objets
   * a retrouver, et ce nombre ne dit pas LESQUELS. C'est le denominateur de la
   * progression — « 7 / 10 » veut dire « tu en as coche 7 sur les 10 a
   * trouver », pas « 7 sont justes ».
   */
  objectsToFind: number;
  /** Textes de la mission, modifiables par l'admin. */
  texts: EventTexts | null;
  /** Le jeune a-t-il deja valide le mot de bienvenue ? */
  welcomeSeen: boolean;
  /**
   * Derniere partie TERMINEE pour chaque jeu, si elle existe. C'est ce qui
   * permet au jeune de revoir son resultat depuis l'accueil, sans avoir a
   * rejouer pour s'en souvenir.
   */
  results: Partial<Record<Game, MissionResult>>;
  /**
   * Etat du concours de fin. Toujours present : c'est `enabled` et `unlocked`
   * qui decident de son affichage, pas l'absence du champ.
   */
  contest: ContestState;
}

/** Le resultat d'une partie terminee : ce que le jeune revoit sur l'accueil. */
export interface MissionResult {
  /** Duree en secondes entre le depart et l'arrivee. */
  durationSeconds: number;
  found: number;
  total: number;
  /**
   * Expedition 1 : nombre de LEURRES coches — des objets qui n'existent pas
   * dans le cosmodome. Toujours 0 pour l'expedition 2, qui n'en a pas.
   *
   * Compte a part et n'entre PAS dans `percent` : un jeune qui a trouve les
   * dix vrais objets a bien tout trouve. Ce sont ses erreurs qui l'empechent
   * d'etre complete, et il faut pouvoir le lui dire separement.
   */
  wrong: number;
  /** Pourcentage entier, 0 a 100. Ne compte que les vrais objets. */
  percent: number;
  /**
   * LE seul critere de « Expedition completee ».
   *
   * Exige le 100 % ET zero erreur. Terminer une expedition ne suffit plus :
   * c'etait le cas avant, et un jeune pouvait afficher « completee » en ayant
   * coche trois objets sur dix.
   */
  completed: boolean;
  /** Fin de la partie, ISO 8601 UTC. */
  endedAt: string;
}

/** Bilan renvoye au clic sur « Fin ». */
export interface MissionSummary extends MissionResult {
  mission: GameMission;
}

// ---------------------------------------------------------------------------
//  Mission 2 — associer chaque dragouille à sa planète
// ---------------------------------------------------------------------------

/** Les deux jeux. Distinct du MODE, qui porte la difficulté de la mission 1. */
export const GAMES = ["objects", "pairing"] as const;
export type Game = (typeof GAMES)[number];

export function isGame(value: unknown): value is Game {
  return typeof value === "string" && (GAMES as readonly string[]).includes(value);
}

/**
 * Un élément de la mission 2, personnage ou astre.
 *
 * NE CONTIENT PAS la bonne réponse : `match_item_id` reste en base et ne sort
 * jamais du serveur.
 */
export interface PairingItem {
  id: string;
  nameI18n: I18nText;
  imageUrl: string | null;
  sortOrder: number;
}

/** Un personnage, avec l'état de sa résolution dans la partie en cours. */
export interface PairingCharacter extends PairingItem {
  /** Déjà associé correctement pendant cette partie. */
  solved: boolean;
  /** L'astre trouvé, une fois résolu. Null tant que ce n'est pas le cas. */
  solvedPlanetId: string | null;
}

export interface PairingStateResponse {
  characters: PairingCharacter[];
  /** Tous les astres proposés, leurres compris, dans un ordre stable. */
  planets: PairingItem[];
  solved: number;
  total: number;
}

/** Réponse à un essai d'association. */
export interface PairingAttemptResponse {
  correct: boolean;
  solved: number;
  total: number;
}

/** Codes d'erreur du jeu. */
export const GAME_ERROR = {
  noActiveEvent: "no_active_event",
  missionAlreadyRunning: "mission_already_running",
  noMissionRunning: "no_mission_running",
  missionEnded: "mission_ended",
  unknownObject: "unknown_object",
  tooManyFinds: "too_many_finds",
} as const;

// ---------------------------------------------------------------------------
//  Concours de fin — la SEULE donnee personnelle collectee par l'application
// ---------------------------------------------------------------------------

/**
 * Le concours ne s'ouvre qu'une fois LES DEUX missions terminees, et seulement
 * si l'admin l'a active sur la mission en cours.
 *
 * C'est le seul endroit ou l'application demande une donnee personnelle. Elle
 * s'adresse a des 6-12 ans : on demande l'adresse d'un ADULTE, le consentement
 * est explicite, et la page qui l'explique est versionnee dans le depot
 * (content/consent.*.md) plutot que modifiable depuis l'administration.
 */
export interface ContestState {
  /** L'admin propose-t-il le concours sur cette mission ? */
  enabled: boolean;
  /** Les deux missions sont-elles terminees ? */
  unlocked: boolean;
  /** Ce joueur a-t-il deja participe ? */
  alreadyEntered: boolean;
}

export interface ContestEntryInput {
  email: string;
  /** La case a cocher. Le serveur REFUSE une participation sans elle. */
  consent: boolean;
}

export const CONTEST_ERROR = {
  /** Le concours n'est pas propose sur cette mission. */
  disabled: "contest_disabled",
  /** Les deux missions ne sont pas terminees. */
  locked: "contest_locked",
  consentRequired: "consent_required",
  invalidEmail: "invalid_email",
  /** Une adresse = une participation. */
  duplicate: "email_already_entered",
} as const;

/** Limite de la norme (RFC 5321). Borne aussi ce qui entre en base. */
export const EMAIL_MAX_LENGTH = 254;

/**
 * Validation d'adresse, volontairement PERMISSIVE.
 *
 * Valider une adresse au sens strict de la RFC est un piege : l'expression
 * complete fait des centaines de caracteres et rejette des adresses valides.
 * On verifie donc la seule chose qui attrape les vraies fautes de frappe —
 * un @, un domaine, un point dans le domaine — et on laisse passer le reste.
 * La seule preuve qu'une adresse existe serait d'y envoyer un courriel.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

export function isValidEmail(value: unknown): value is string {
  return typeof value === "string" && value.length <= EMAIL_MAX_LENGTH && EMAIL_PATTERN.test(value);
}

/**
 * Forme comparee pour detecter les doublons : espaces retires, minuscules.
 *
 * On NE touche pas a la partie locale au-dela de la casse (pas de retrait des
 * points facon Gmail) : ces regles appartiennent a chaque fournisseur, et les
 * appliquer partout refuserait des adresses legitimement distinctes.
 */
export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * Un joueur, tel que l'administration le voit.
 *
 * Le compte lui-meme ne porte AUCUNE donnee personnelle : un pseudo, un NIP
 * hache, des dates. `contestEmail` n'appartient pas au compte — c'est l'adresse
 * qu'un adulte a donnee pour le concours de la mission consultee, rapprochee
 * ici pour que l'organisateur puisse la corriger ou la retirer sur demande,
 * comme la page de consentement le promet.
 *
 * Elle est donc PAR MISSION : le meme joueur n'a pas d'adresse sur une mission
 * ou il n'a pas participe.
 */
export interface AdminUser {
  id: string;
  pseudo: string;
  createdAt: string;
  /** Quand le mot de bienvenue a ete valide. NULL = jamais ouvert. */
  welcomeSeenAt: string | null;
  /** Parties terminees sur la mission consultee. */
  missionsPlayed: number;
  /** Adresse donnee pour le concours de cette mission, ou null. */
  contestEmail: string | null;
  /** Identifiant de la participation, s'il y en a une. */
  contestEntryId: string | null;
}

export const USER_ADMIN_ERROR = {
  pseudoTaken: "pseudo_taken",
  invalidPseudo: "invalid_pseudo",
  invalidNip: "invalid_nip",
  /**
   * Aucune participation a modifier. L'administration ne CREE pas d'adresse :
   * ce serait enregistrer un consentement que personne n'a donne.
   */
  noContestEntry: "no_contest_entry",
  emailTaken: "email_already_entered",
} as const;

/** Une participation, telle que l'administration la voit. */
export interface AdminContestEntry {
  id: string;
  email: string;
  /** Langue de la page de consentement acceptee. */
  lang: Lang;
  /** Empreinte du texte de consentement en vigueur au moment du clic. */
  consentVersion: string;
  createdAt: string;
}

/** Codes d'erreur propres a la gestion de contenu. */
export const CONTENT_ERROR = {
  notFound: "not_found",
  invalidInput: "invalid_input",
  /** Un autre objet reel du meme event porte deja ce code. */
  codeTaken: "code_taken",
  /** La cible choisie n'appartient pas a l'event de l'objet reel. */
  targetOutsideEvent: "target_outside_event",
  imageTooLarge: "image_too_large",
  imageBadFormat: "image_bad_format",
} as const;

/**
 * Reglages publics servis par `GET /api/config`.
 * Rien de sensible : uniquement ce dont le client a besoin au demarrage.
 */
export interface AppConfigResponse {
  /** Langue par defaut du site, definie par DEFAULT_LANG dans le .env. */
  defaultLang: Lang;
  /**
   * Titre et sous-titre du site, definis par l'admin dans les textes de la
   * mission active. Servis sur une route PUBLIQUE parce qu'ils coiffent
   * l'ecran de connexion, avant toute authentification. Ce sont des textes
   * d'habillage, rien de confidentiel.
   *
   * null quand aucune mission n'est active : le client retombe alors sur ses
   * propres traductions.
   */
  appTitle: I18nText | null;
  appSubtitle: I18nText | null;
}

/** Enveloppe d'erreur unique de l'API. Toute erreur serveur sort sous cette forme. */
export interface ApiError {
  error: {
    /** Code stable, exploitable par le client pour choisir une cle i18n. */
    code: string;
    /** Message technique, en anglais, destine aux logs et non au jeune. */
    message: string;
  };
}

// ---------------------------------------------------------------------------
//  Statistiques — page /stat, troisieme role
//
//  Ce role ne voit AUCUNE donnee personnelle : il compte. Ni pseudo, ni adresse
//  courriel ne circulent sur ces routes — le telechargement des participants
//  reste derriere le mot de passe admin, la ou il etait.
// ---------------------------------------------------------------------------

/** Echelle de l'axe du temps. */
export const STATS_SCALES = ["hour", "day", "week", "month"] as const;
export type StatsScale = (typeof STATS_SCALES)[number];

export function isStatsScale(value: unknown): value is StatsScale {
  return typeof value === "string" && (STATS_SCALES as readonly string[]).includes(value);
}

/**
 * Les cinq mesures, DANS CET ORDRE.
 *
 * L'ordre n'est pas cosmetique : il fixe l'attribution des couleurs, et la
 * palette a ete validee sur CETTE suite. Les teintes voisines y sont separees
 * au sens du daltonisme ; permuter deux mesures casse cette garantie.
 */
export const STATS_METRICS = [
  "users",
  "expedition1",
  "expedition2",
  "mission",
  "contest",
] as const;
export type StatsMetric = (typeof STATS_METRICS)[number];

/** Un intervalle de temps et ce qui s'y est produit. */
export interface StatsPoint {
  /** Debut de l'intervalle, ISO 8601 UTC. */
  bucket: string;
  counts: Record<StatsMetric, number>;
}

export interface StatsSeriesResponse {
  scale: StatsScale;
  points: StatsPoint[];
  /** Cumul sur toute la periode, pour les cartouches de tete. */
  totals: Record<StatsMetric, number>;
}

/** Une etape de l'entonnoir : combien de joueurs l'ont atteinte. */
export interface StatsFunnelStep {
  etape: string;
  joueurs: number;
}

/** Un objet et sa frequence — vrai objet trouve, ou leurre coche. */
export interface StatsObject {
  nom: string;
  /** Joueurs distincts l'ayant coche au moins une fois. */
  joueurs: number;
  /** Part des joueurs ayant joue l'expedition 1, en pourcentage entier. */
  pourcent: number;
}

/** Une dragouille et ce qu'elle a coute aux jeunes. */
export interface StatsPairing {
  nom: string;
  /** Joueurs l'ayant associee a la bonne planete. */
  resolue: number;
  /** Essais rates, tous joueurs confondus. */
  rates: number;
}

export interface StatsDuration {
  /** Parties terminees prises en compte. */
  parties: number;
  medianeSecondes: number;
  minSecondes: number;
  maxSecondes: number;
}

export interface StatsSummary {
  funnel: StatsFunnelStep[];
  /** Les vrais objets, du moins trouve au plus trouve. */
  objets: StatsObject[];
  /** Les leurres, du plus coche au moins coche. */
  leurres: StatsObject[];
  /** Les dragouilles, de la plus couteuse en essais a la moins couteuse. */
  associations: StatsPairing[];
  durees: Record<"expedition1" | "expedition2", StatsDuration>;
  /** Intervalle d'une heure ou le plus de parties ont demarre. */
  pointe: { bucket: string; parties: number } | null;
}
